import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { mapLayers, setDisplayLegendLayer, thumbnailTable, webMap } from "@/components/PQMap/pqMapSlice";
import { db } from "@/lib/db";
import type { LayerListGroup, LayerState } from "@/types";
import { ActionIcon, Box, Divider, Grid, Image, ScrollArea, Stack, Text, Title } from "@mantine/core";
import { IconList } from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo } from "react";
import { createLayerList, layerListLoading } from "./layerListSlice";

/** Groups layers by their WebMap group, reversed so the top-most layers come first. */
const groupLayers = (layers: readonly LayerState[]): LayerListGroup[] => {
    const groups = new Map<string, LayerState[]>();
    for (const layer of layers) groups.set(layer.group, [...(groups.get(layer.group) ?? []), layer]);
    return [...groups].map(([name, groupLayers]) => ({ name, layers: groupLayers.reverse() })).reverse();
}

const LayerList: React.FC = () => {
    const dispatch = useAppDispatch();
    const currentMapLayers = useAppSelector(mapLayers);
    const currentThumbnailTable = useAppSelector(thumbnailTable);
    const currentWebMap = useAppSelector(webMap)
    const loading = useAppSelector(layerListLoading)
    const layerStates = useLiveQuery(() => db.layerState.toArray());
    const layerGroups = useMemo(() => groupLayers(layerStates ?? []), [layerStates]);

    const onLayerLegendClickHandler = (layer: LayerState) => {
        dispatch(setDisplayLegendLayer(layer));
    }

    const onLayerVisibilityClickHandler = async (layer: LayerState) => {
        await db.layerState.update(layer.id, { visible: !layer.visible });
        const mapLayer = currentWebMap?.findLayerById(layer.layerId);
        if (mapLayer) {
            mapLayer.visible = !mapLayer.visible
        }
    }

    useEffect(() => {
        if (currentMapLayers.length > 0 && currentThumbnailTable) {
            dispatch(createLayerList({ layers: [...currentMapLayers], thumbnailTable: currentThumbnailTable }));
        }
    }, [currentMapLayers, currentThumbnailTable, dispatch])

    return (
        <ScrollArea h='100%' pt={5} pb={60} type='never'>
            {loading ? <Text>Loading...</Text> : (
                <Stack>
                    {layerGroups.map((group) => (
                        <Box mx={10} mb={10} key={group.name}>
                            <Title order={5} mb={5}>{group.name}</Title>
                            <Grid gutter={2}>
                                {group.layers.map((layer) => (
                                    <Grid.Col span={{ base: 6, md: 4, lg: 3 }} key={layer.id}>
                                        <Box style={{ borderRadius: '10px', overflow: 'hidden', border: `3px solid ${layer.visible ? 'green' : '#B3B3B3'}` }} bg='#E8E8E8'>
                                            <Stack gap={0}>
                                                <Box onClick={() => onLayerVisibilityClickHandler(layer)} style={{ borderRadius: '10px 10px 0 0', overflow: 'hidden' }}>
                                                    {layer.imgSrc && <Image src={layer.imgSrc} alt={layer.title} />}
                                                </Box>
                                                <Divider color="#B3B3B3" size={3} />
                                                <Grid align="center">
                                                    <Grid.Col onClick={() => onLayerVisibilityClickHandler(layer)} span={9}>
                                                        <Text size="sm" ml={10} fw={600} >{layer.title}</Text>
                                                    </Grid.Col>
                                                    <Grid.Col style={{ textAlign: 'right', paddingRight: '15px' }} span={3}>
                                                        <ActionIcon mt={5} size='xs' color="black" onClick={() => onLayerLegendClickHandler(layer)} aria-label={`Show ${layer.title} legend`}>
                                                            <IconList />
                                                        </ActionIcon>
                                                    </Grid.Col>
                                                </Grid>
                                            </Stack>
                                        </Box>
                                    </Grid.Col>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Stack>
            )}
        </ScrollArea>
    )
}

export default LayerList;
